<template>
  <Card>
    <TitleChapterListFilters
      v-model:sortDesc="sortDesc"
      v-model:translationLocale="translationLocale"
      :all-translation-locales="allTranslationLocales"
      :loading="isPending || translationPending"
    />
    <div v-if="isPending" class="space-y-4">
      <div class="space-y-4">
        <Skeleton class="h-[4.5rem] w-full" />
        <Skeleton class="h-[4.5rem] w-full" />
        <Skeleton class="h-[4.5rem] w-full" />
      </div>
    </div>
    <div v-else-if="error">
      {{ error }}
    </div>
    <div v-else-if="data">
      <TransitionAutoHeight :model-value="selecting">
        <div class="flex justify-end space-x-2 pb-4">
          <NamiButton
            button-type="secondary"
            small
            @buttonClick="selecting = false"
          >
            {{ $t("components.chaptersList.buttonCancel") }}
          </NamiButton>
          <NamiButton button-type="secondary" small @buttonClick="markRead">
            {{ $t("components.chaptersList.buttonMarkRead") }}
          </NamiButton>
          <NamiButton button-type="secondary" small @buttonClick="markUnread">
            {{ $t("components.chaptersList.buttonMarkUnread") }}
          </NamiButton>
        </div>
      </TransitionAutoHeight>
      <p v-if="data.data.length === 0">
        {{ $t("site.title.id.slug.chapters.textNoChaptersPublished") }}
      </p>

      <div v-else class="grid auto-rows-1fr responsive-gap">
        <ListSelect
          v-model="selectedItems"
          :listItems="data.data"
          :show-select="selecting"
        >
          <template #listComponent="{ item, toggle }">
            <ChapterCard
              class="w-full"
              :class="{ 'select-none': selecting }"
              :selecting="selecting"
              :chapter="item"
              :chapterKind="titleEntity.attributes.chapterKind"
              :volumeKind="titleEntity.attributes.volumeKind"
              :title-cover-url="coverUrl"
              :is-read="!!readMarkers?.[item.id]"
              :is-locked="chapterLocks && !chapterLocks?.[item.id]"
              :is-access="!!userAccess?.[item.id]"
              selectable
              no-lang
              @click="() => selecting && toggle()"
              @selectAll="beginSelect"
            />
            <Skeleton v-if="isPlaceholderData" class="absolute inset-0" />
          </template>
        </ListSelect>
      </div>

      <NamiPagination
        v-model="page"
        class="mt-4"
        :total-pages="Math.ceil(data.meta.total / limit)"
        :total-items="data.meta.total"
      />
    </div>
  </Card>
</template>

<script setup lang="ts">
import {
  type AvailableLanguages,
  Chapter,
  getId,
  type TitleEntity,
} from "~/src/api";
import { getLanguages } from "~/utils/static";
import { parseTitleForDisplay } from "~/utils/display/title";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";

const nuxtApp = useNuxtApp();
const settingsStore = nuxtApp.$settings();
const { locale } = useI18n();
const cacheTimings = useCacheTimings();
const queryClient = useQueryClient();

const selecting = ref(false);
const selectedItems = ref({});

const { titleEntity: title } = defineProps<{
  titleEntity: TitleEntity;
}>();

const translationLocale = defineModel<AvailableLanguages>("translationLocale", {
  required: true,
});

const titleId = title.id;
const { coverUrl } = parseTitleForDisplay(title, "large");

const {
  data: translations,
  isPending: translationPending,
  suspense: translationPrefetch,
} = useQuery({
  queryKey: ["chapter_languages", "entity", titleId],
  queryFn: async () => {
    return await Chapter.getAvailableLanguages(titleId);
  },
  staleTime: cacheTimings.entity.chapter_languages,
});

const allLanguages = getLanguages();

const allTranslationLocales = computed(() => {
  const availableLangs =
    translations.value?.data.aggregatedLanguageCounts ?? {};
  const translationKeys = [
    ...new Set([...Object.keys(availableLangs), locale.value]),
  ];
  const locales = settingsStore?.settings.platform
    .preferredTranslationLocales ?? [locale.value];

  return allLanguages
    .filter(
      (option) =>
        translationKeys.includes(option.value) &&
        (locales.length > 0 ? locales.includes(option.value) : true),
    )
    .map((lang) => ({
      text: `${lang.text} (${availableLangs[lang.value] ?? 0})`,
      value: lang.value,
    }));
});

watch(allTranslationLocales, (newList) => {
  const availableLangs = newList.map((locales) => locales.value);
  if (availableLangs.includes(translationLocale.value)) return;
  translationLocale.value = availableLangs.at(0) ?? "en";
});

// API fields
const { page, limit, offset } = usePagination(1, 32);
const sortDesc = ref(true);

const {
  data,
  error,
  isPending,
  isPlaceholderData,
  suspense: listPrefetch,
} = useQuery({
  ...useChapterList(
    computed(() => ({
      titleIds: [titleId],
      order: { natural: sortDesc.value ? "desc" : "asc" },
      limit: limit.value,
      offset: offset.value,
      translatedLanguages: [translationLocale.value],
    })),
  ),
  placeholderData: keepPreviousData,
  staleTime: cacheTimings.entity.chapter,
});

watch(data, () => {
  selecting.value = false;
  selectedItems.value = {};
});

// read markers
const { markerQuery, markerMutation } = useChapterReadMarkerManager(
  queryClient,
  titleId,
);
const { data: readMarkers } = markerQuery;
const { mutate: updateReadMarkers } = markerMutation;

function beginSelect() {
  if (!data.value) return;
  selecting.value = true;
  selectedItems.value = Object.fromEntries(
    data.value.data.map((_, index) => [index, true]),
  );
}

const markRead = () => {
  if (!data.value) return;
  const selectedChapterIds = Object.entries(selectedItems.value)
    .filter(([idx, sel]) => sel)
    .map(([idx, sel]) => data.value.data[parseInt(idx)]?.id)
    .filter(onlyTruthys);

  updateReadMarkers({
    add: selectedChapterIds,
  });

  selectedItems.value = {};
  selecting.value = false;
};

const markUnread = () => {
  if (!data.value) return;
  const selectedChapterIds = Object.entries(selectedItems.value)
    .filter(([idx, sel]) => sel)
    .map(([idx, sel]) => data.value.data[parseInt(idx)]?.id)
    .filter(onlyTruthys);

  updateReadMarkers({
    remove: selectedChapterIds,
  });

  selectedItems.value = {};
  selecting.value = false;
};

// gating
const authStore = useAuthStore();
const loggedIn = useIsLoggedIn();

const { data: chapterLocks, suspense: lockPrefetch } = useQuery(
  useGatingAccess(
    computed(() => data.value?.data.map(getId) ?? []),
    "chapter",
  ),
);

const { data: userAccess } = useQuery({
  ...useGatingAccess(
    computed(() => data.value?.data.map(getId) ?? []),
    "chapter",
    authStore.getToken,
  ),
  enabled: loggedIn,
});

onServerPrefetch(async () => {
  await Promise.all([translationPrefetch(), listPrefetch()]);
  await lockPrefetch();
});
</script>
